import { Trans } from '@lingui/react'
import { isNil } from 'ramda'

import type {
  InstallmentSpecificContractTerms,
  PlInstallmentsContractTerms,
} from '@twisto/api-graphql'
import { Flex } from '@twisto/components/atoms/flex'
import {
  FormattedCurrencyNumber,
  FormattedNumber,
} from '@twisto/components/atoms/format'
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@twisto/components/atoms/table'
import { Typography } from '@twisto/components/atoms/typography'
import { numberOfDays } from '@twisto/locale'
import { styleUtils } from '@twisto/styles'
import { isObject } from '@twisto/utils'

import * as styles from './installments-contract-terms-table.styles'

type Props = {
  installmentContractTerms:
    | Omit<InstallmentSpecificContractTerms, '__typename'>
    | Omit<PlInstallmentsContractTerms, '__typename' | 'id'>
  className?: string
}

const extractInstallmentContractProps = (
  terms: Props['installmentContractTerms']
) => {
  if ('annualPercentageRate' in terms) {
    return {
      customerAnnualPercentageRate: terms.annualPercentageRate,
      termDays: terms.contractTermDays,
      installmentsLimit: terms.installmentsLimit,
      capitalInterestRate: terms.interestRate,
      maxMonthlyFee: terms.maximumMonthlyFee,
      maxAnnualFee: terms.maximumTotalFee,
      paymentsLimit: terms.paymentsLimit,
    }
  }

  return terms
}

export const InstallmentsContractTermsTable = ({
  installmentContractTerms,
  className,
}: Props) => {
  const {
    paymentsLimit,
    installmentsLimit,
    maxMonthlyFee,
    customerAnnualPercentageRate,
    maxAnnualFee,
    capitalInterestRate,
    termDays,
  } = extractInstallmentContractProps(installmentContractTerms)

  const termValues = [
    {
      title: <Trans id="flow.components.contractTermsTable.twistoLimit" />,
      value: (
        <FormattedCurrencyNumber
          value={Number(paymentsLimit) + Number(installmentsLimit)}
        />
      ),
    },
    {
      title: <Trans id="flow.components.contractTermsTable.paymentLimit" />,
      value: <FormattedCurrencyNumber value={paymentsLimit} />,
      indent: true,
    },
    {
      title: (
        <Trans id="flow.components.contractTermsTable.installmentsLimit" />
      ),
      value: <FormattedCurrencyNumber value={installmentsLimit} />,
      indent: true,
    },
    !isNil(termDays) && {
      title: <Trans id="flow.components.contractTermsTable.termDays" />,
      value: numberOfDays(termDays),
    },
    !isNil(maxMonthlyFee) && {
      title: <Trans id="flow.components.contractTermsTable.maxMonthlyFee" />,
      value: <FormattedCurrencyNumber value={maxMonthlyFee} />,
    },
    !isNil(maxAnnualFee) && {
      title: <Trans id="flow.components.contractTermsTable.maxAnnualFee" />,
      value: <FormattedCurrencyNumber value={maxAnnualFee} />,
    },
    !isNil(customerAnnualPercentageRate) && {
      title: <Trans id="flow.components.contractTermsTable.apr" />,
      value: (
        <FormattedNumber
          maximumFractionDigits={2}
          minimumFractionDigits={2}
          style="percent"
          value={customerAnnualPercentageRate}
        />
      ),
    },
    !isNil(capitalInterestRate) && {
      title: <Trans id="flow.components.contractTermsTable.capitalRate" />,
      value: (
        <FormattedNumber
          maximumFractionDigits={2}
          minimumFractionDigits={2}
          style="percent"
          value={capitalInterestRate}
        />
      ),
    },
  ]

  return (
    <Flex
      alignItems="flexStart"
      className={className}
      css={styles.container}
      direction="column"
    >
      <Typography align="start" variant="b3">
        <Trans id="flow.components.contractTermsTable.title" />
      </Typography>
      {!isNil(termDays) && (
        <Typography
          align="start"
          color="textSecondary"
          css={styleUtils.margin.top.sm}
          variant="b4"
        >
          <Trans
            id="flow.components.contractTermsTable.perex"
            values={{ termDays: numberOfDays(termDays) }}
          />
        </Typography>
      )}

      <Table css={styleUtils.margin.top.md}>
        <TableBody>
          {termValues
            .filter<Exclude<(typeof termValues)[number], false>>(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              isObject as any
            )
            .map(({ title, value, indent = false }, index) => (
              <TableRow key={index}>
                <TableCell css={[indent && styles.cellIndent]} scope="row">
                  <Typography color="textSecondary" variant="b4">
                    {title}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    color={indent ? 'textSecondary' : 'textPrimary'}
                    variant="b4"
                  >
                    {value}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </Flex>
  )
}
